/* eslint-disable ember/no-classic-components */
import Component, { Input } from "@ember/component";
import { hash } from "@ember/helper";
import { action, computed } from "@ember/object";
import { service } from "@ember/service";
import { isEmpty } from "@ember/utils";
import { tagName } from "@ember-decorators/component";
import { popupAjaxError } from "discourse/lib/ajax-error";
import BufferedProxy from "discourse/lib/buffered-proxy";
import { AUTO_GROUPS } from "discourse/lib/constants";
import PermissionType from "discourse/models/permission-type";
import GroupChooser from "discourse/select-kit/components/group-chooser";
import TagChooser from "discourse/select-kit/components/tag-chooser";
import DButton from "discourse/ui-kit/d-button";
import DRadioButton from "discourse/ui-kit/d-radio-button";
import DTextField from "discourse/ui-kit/d-text-field";
import { i18n } from "discourse-i18n";

@tagName("")
export default class TagGroupsForm extends Component {
  @service dialog;
  @service site;

  // All but the "everyone" group
  allGroups = this.site.groups.filter(
    ({ id }) => id !== AUTO_GROUPS.everyone.id
  );

  @computed("model")
  get buffered() {
    return BufferedProxy.create({
      content: this.model,
    });
  }

  @computed("buffered.permissions")
  get selectedGroupIds() {
    const permissions = this.get("buffered.permissions"); // TODO (devxp) we need a buffered proxy that works with tracked properties
    if (!permissions) {
      return [];
    }

    let groupIds = [];

    for (const [groupId, permission] of Object.entries(permissions)) {
      // JS object keys are always strings, so we need to convert them to integers
      const id = parseInt(groupId, 10);

      if (
        id !== AUTO_GROUPS.everyone.id &&
        permission === PermissionType.FULL
      ) {
        groupIds.push(id);
      }
    }

    return groupIds;
  }

  @action
  setPermissionsGroups(groupIds) {
    let permissions = {};
    groupIds.forEach((id) => (permissions[id] = PermissionType.FULL));
    this.buffered.set("permissions", permissions);
  }

  @action
  save() {
    const attrs = this.buffered.getProperties(
      "name",
      "tags",
      "parent_tag",
      "one_per_topic",
      "permissions"
    );

    if (attrs.tags) {
      attrs.tags = attrs.tags.map(this.#serializeTag);
    }

    if (attrs.parent_tag) {
      attrs.parent_tag = attrs.parent_tag.map(this.#serializeTag);
    }

    if (isEmpty(attrs.name)) {
      this.dialog.alert(i18n("tagging.groups.cannot_save.empty_name"));
      return false;
    }

    if (isEmpty(attrs.tags)) {
      this.dialog.alert(i18n("tagging.groups.cannot_save.no_tags"));
      return false;
    }

    attrs.permissions = { ...(attrs.permissions ?? {}) };

    const permissionName = this.buffered.get("permissionName");

    if (permissionName === "public") {
      attrs.permissions = { 0: PermissionType.FULL };
    } else if (permissionName === "visible") {
      attrs.permissions[0] = PermissionType.READONLY;
    } else if (permissionName === "private") {
      delete attrs.permissions[0];

      const hasGroups = Object.keys(attrs.permissions).some(
        (k) => parseInt(k, 10) !== AUTO_GROUPS.everyone.id
      );
      if (!hasGroups) {
        this.dialog.alert(i18n("tagging.groups.cannot_save.no_groups"));
        return false;
      }
    }

    this.model
      .save(attrs)
      .then(() => this.onSave?.())
      .catch(popupAjaxError);
  }

  @action
  destroyTagGroup() {
    return this.dialog.yesNoConfirm({
      message: i18n("tagging.groups.confirm_delete"),
      didConfirm: () =>
        this.model.destroyRecord().then(() => this.onDestroy?.()),
    });
  }

  #serializeTag(t) {
    return typeof t.id === "number"
      ? { id: t.id, name: t.name }
      : { name: t.name };
  }

  <template>
    <section class="group-name">
      <label>{{i18n "tagging.groups.name_placeholder"}}</label>
      <div>
        <DTextField @value={{this.buffered.name}} /></div>
    </section>

    <section class="group-tags-list">
      <label>{{i18n "tagging.groups.tags_label"}}</label><br />
      <TagChooser
        @everyTag={{true}}
        @excludeSynonyms={{true}}
        @options={{hash
          allowAny=true
          filterPlaceholder="tagging.groups.tags_placeholder"
        }}
        @tags={{this.buffered.tags}}
        @unlimitedTagCount={{true}}
      />
    </section>

    <section class="parent-tag-section">
      <label>{{i18n "tagging.groups.parent_tag_label"}}</label>
      <div>
        <TagChooser
          @everyTag={{true}}
          @excludeSynonyms={{true}}
          @options={{hash
            allowAny=true
            filterPlaceholder="tagging.groups.parent_tag_placeholder"
            maximum=1
          }}
          @tags={{this.buffered.parent_tag}}
        />
      </div>
      <div class="description">{{i18n
          "tagging.groups.parent_tag_description"
        }}</div>
    </section>

    <section class="group-one-per-topic">
      <label>
        <Input
          name="onepertopic"
          @checked={{this.buffered.one_per_topic}}
          @type="checkbox"
        />
        {{i18n "tagging.groups.one_per_topic_label"}}
      </label>
    </section>

    <section class="group-visibility">
      <div class="group-visibility-option">
        <DRadioButton
          class="tag-permissions-choice"
          @id="public-permission"
          @name="tag-permissions-choice"
          @selection={{this.buffered.permissionName}}
          @value="public"
        />

        <label class="radio" for="public-permission">
          {{i18n "tagging.groups.everyone_can_use"}}
        </label>
      </div>
      <div class="group-visibility-option">
        <DRadioButton
          class="tag-permissions-choice"
          @id="visible-permission"
          @name="tag-permissions-choice"
          @selection={{this.buffered.permissionName}}
          @value="visible"
        />

        <label class="radio" for="visible-permission">
          {{i18n "tagging.groups.usable_only_by_groups"}}
        </label>

        <div class="group-access-control">
          <GroupChooser
            @content={{this.allGroups}}
            @labelProperty="name"
            @onChange={{this.setPermissionsGroups}}
            @options={{hash
              filterPlaceholder="tagging.groups.select_groups_placeholder"
            }}
            @value={{this.selectedGroupIds}}
          />
        </div>
      </div>
      <div class="group-visibility-option">
        <DRadioButton
          class="tag-permissions-choice"
          @id="private-permission"
          @name="tag-permissions-choice"
          @selection={{this.buffered.permissionName}}
          @value="private"
        />

        <label class="radio" for="private-permission">
          {{i18n "tagging.groups.visible_only_to_groups"}}
        </label>

        <div class="group-access-control">
          <GroupChooser
            @content={{this.allGroups}}
            @labelProperty="name"
            @onChange={{this.setPermissionsGroups}}
            @options={{hash
              filterPlaceholder="tagging.groups.select_groups_placeholder"
            }}
            @value={{this.selectedGroupIds}}
          />
        </div>
      </div>
    </section>

    <div class="tag-group-controls">
      <DButton
        class="btn-primary"
        @action={{this.save}}
        @disabled={{this.buffered.isSaving}}
        @label="tagging.groups.save"
      />

      <DButton
        class="btn-danger"
        @action={{this.destroyTagGroup}}
        @disabled={{this.buffered.isNew}}
        @icon="trash-can"
        @label="tagging.groups.delete"
      />
    </div>
  </template>
}
